import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  } catch { return ""; }
}

export default function MarketNews() {
  const [posts, setPosts] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/news").then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  return (
    <section id="news" data-testid="news-section" className="relative bg-[#0A0A0A] py-32 md:py-44 px-6 md:px-12 border-t border-white/5">
      <div className="mx-auto max-w-[1500px]">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end mb-16 md:mb-24">
          <div className="md:col-span-8">
            <p className="overline text-[#CBBFAE] mb-6">The Journal</p>
            <h2 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight text-white">
              Central Valley <em className="text-[#CBBFAE]">market news</em> for you.
            </h2>
          </div>
          <div className="md:col-span-4 text-white/60 text-base leading-relaxed">
            Learn more and stay up to date on the current market conditions in California.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
          {posts.map((p, i) => (
            <motion.article
              key={p.id}
              data-testid="news-article-card"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ duration: 0.8, delay: i * 0.06 }}
              onClick={() => setActive(p)}
              className="group cursor-pointer bg-[#0A0A0A] hover:bg-[#121212] transition-colors p-8 md:p-10 min-h-[380px] flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between overline text-white/40 mb-8">
                  <span>{p.category}</span>
                  <span>{fmtDate(p.published_at)}</span>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl leading-tight text-white group-hover:text-[#CBBFAE] transition-colors mb-4">
                  {p.title}
                </h3>
                {p.source && <div className="overline text-[#CBBFAE] mb-5">{p.source}</div>}
                <p className="text-white/60 text-sm md:text-base leading-relaxed line-clamp-4">{p.excerpt}</p>
              </div>
              <div className="mt-8 flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-white/60 group-hover:text-[#CBBFAE] transition-colors">
                <span className="h-px w-8 bg-current" />
                Read More
              </div>
            </motion.article>
          ))}
        </div>
      </div>

      {/* Simple modal */}
      {active && (
        <div
          data-testid="news-modal"
          className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
          onClick={() => setActive(null)}
        >
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="max-w-2xl w-full bg-[#121212] border border-white/10 p-8 md:p-14 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between overline text-white/50 mb-8">
              <span>{active.category}</span>
              <button data-testid="news-modal-close" onClick={() => setActive(null)} className="text-white/70 hover:text-[#CBBFAE] tracking-[0.2em]">Close ✕</button>
            </div>
            <h3 className="font-serif text-3xl md:text-5xl leading-tight text-white mb-6">{active.title}</h3>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 overline text-[#CBBFAE] mb-8">
              {active.source && <span>{active.source}</span>}
              <span className="text-white/40">{fmtDate(active.published_at)}</span>
            </div>
            <p className="text-white/75 leading-relaxed whitespace-pre-line">{active.body}</p>
            {active.link && (
              <a
                data-testid="news-modal-read-more"
                href={active.link}
                target="_blank"
                rel="noopener noreferrer"
                className="group mt-10 inline-flex items-center gap-4 border border-[#CBBFAE]/50 hover:border-[#CBBFAE] rounded-full pl-6 pr-2 py-2 text-[12px] tracking-[0.25em] uppercase text-white transition-all"
              >
                <span>Read More</span>
                <span className="w-9 h-9 rounded-full bg-[#CBBFAE] text-[#0A0A0A] flex items-center justify-center transition-transform group-hover:rotate-45">↗</span>
              </a>
            )}
          </motion.div>
        </div>
      )}
    </section>
  );
}
